import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Plus, Trash2, Edit2, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

const CATEGORY_CONFIG = {
  pronoun: {
    title: 'Pronouns',
    desc: 'Personal, demonstrative, and other pronouns',
    labelPlaceholder: 'e.g., first person singular',
    info: 'Pronouns replace nouns and vary by person, number, gender, and case in many languages.',
    extraFields: ['person', 'number', 'gender', 'case'],
  },
  number: {
    title: 'Numbers',
    desc: 'Cardinal and ordinal numbers',
    labelPlaceholder: 'e.g., one, two, three',
    info: 'Number systems vary widely: base-10, base-20, body-part counting, and more.',
    extraFields: ['numeric_value'],
  },
  kinship: {
    title: 'Kinship Terms',
    desc: 'Family and relationship terms',
    labelPlaceholder: 'e.g., mother, father, sibling',
    info: 'Kinship systems reflect cultural distinctions. Some languages distinguish maternal/paternal relatives, age-based terms, or have no separate word for "cousin".',
    extraFields: ['relationship_type'],
  },
  color: {
    title: 'Color Terms',
    desc: 'Basic and extended color vocabulary',
    labelPlaceholder: 'e.g., red, blue, dark green',
    info: 'Languages vary in how they divide the color spectrum. Some have only 2-3 basic color terms, while others have many.',
    extraFields: [],
  },
  body_part: {
    title: 'Body Parts',
    desc: 'Anatomical vocabulary',
    labelPlaceholder: 'e.g., hand, head, eye',
    info: 'Body part terms are among the most universal vocabulary. Many languages use body parts metaphorically for spatial relations.',
    extraFields: [],
  },
};

const LexiconCategoryPage = ({ category }) => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const config = CATEGORY_CONFIG[category] || { title: category, desc: '', labelPlaceholder: '', info: '', extraFields: [] };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [label, setLabel] = useState('');
  const [word, setWord] = useState('');
  const [ipa, setIpa] = useState('');
  const [notes, setNotes] = useState('');
  const [person, setPerson] = useState('');
  const [number, setNumber] = useState('');
  const [gender, setGender] = useState('');
  const [caseField, setCaseField] = useState('');
  const [numericValue, setNumericValue] = useState('');
  const [relationshipType, setRelationshipType] = useState('');

  useEffect(() => { fetchData(); }, [conlangId, category]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/lexicon-category/${category}`, { withCredentials: true });
      setItems(res.data);
    } catch { toast.error(`Failed to load ${config.title.toLowerCase()}`); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setLabel(''); setWord(''); setIpa(''); setNotes('');
    setPerson(''); setNumber(''); setGender(''); setCaseField('');
    setNumericValue(''); setRelationshipType('');
    setEditingItem(null);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setLabel(item.label); setWord(item.word); setIpa(item.ipa || ''); setNotes(item.notes || '');
    setPerson(item.person || ''); setNumber(item.number || ''); setGender(item.gender || '');
    setCaseField(item.case || ''); setNumericValue(item.numeric_value?.toString() || '');
    setRelationshipType(item.relationship_type || '');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const data = {
      category, label, word,
      ipa: ipa || null, notes: notes || null,
      person: person || null, number: number || null,
      gender: gender || null, case: caseField || null,
      numeric_value: numericValue ? parseInt(numericValue) : null,
      relationship_type: relationshipType || null,
    };
    try {
      if (editingItem) {
        const res = await axios.put(`${API}/conlangs/${conlangId}/lexicon-category/${editingItem.item_id}`, data, { withCredentials: true });
        setItems(items.map(i => i.item_id === editingItem.item_id ? res.data : i));
        toast.success('Item updated');
      } else {
        const res = await axios.post(`${API}/conlangs/${conlangId}/lexicon-category`, data, { withCredentials: true });
        setItems([...items, res.data]);
        toast.success('Item added');
      }
      setDialogOpen(false);
      resetForm();
    } catch { toast.error('Failed to save'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/lexicon-category/${id}`, { withCredentials: true });
      setItems(items.filter(i => i.item_id !== id));
      toast.success('Item deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid={`${category}-title`}>{config.title}</h2>
          <p className="text-foreground-muted mt-1">{config.desc} in {conlang?.name}</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary hover:bg-primary/90" data-testid={`add-${category}-btn`}>
          <Plus className="w-4 h-4 mr-2" /> Add {config.title.slice(0, -1)}
        </Button>
      </div>

      {items.length > 0 ? (
        <div className="space-y-2" data-testid={`${category}-list`}>
          {items.map(item => (
            <Card key={item.item_id} className="bg-background-paper border-border hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-conlang text-lg text-foreground font-semibold">{item.word}</span>
                      {item.ipa && <span className="font-conlang text-secondary text-sm">{item.ipa}</span>}
                    </div>
                    <p className="text-foreground-muted text-sm">{item.label}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {item.person && <span className="text-xs bg-accent px-2 py-0.5 rounded text-foreground-muted">{item.person}</span>}
                      {item.number && <span className="text-xs bg-accent px-2 py-0.5 rounded text-foreground-muted">{item.number}</span>}
                      {item.gender && <span className="text-xs bg-accent px-2 py-0.5 rounded text-foreground-muted">{item.gender}</span>}
                      {item.case && <span className="text-xs bg-accent px-2 py-0.5 rounded text-foreground-muted">{item.case}</span>}
                      {item.numeric_value != null && <span className="text-xs bg-accent px-2 py-0.5 rounded text-foreground-muted">= {item.numeric_value}</span>}
                      {item.relationship_type && <span className="text-xs bg-accent px-2 py-0.5 rounded text-foreground-muted">{item.relationship_type}</span>}
                    </div>
                    {item.notes && <p className="text-xs text-foreground-subtle mt-1">{item.notes}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.item_id)} className="text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-background-paper border-border border-dashed">
          <CardContent className="p-12 text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">No {config.title.toLowerCase()} yet</h3>
            <p className="text-foreground-muted mb-4">Start building your {config.title.toLowerCase()} vocabulary.</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" /> Add {config.title.slice(0, -1)}</Button>
          </CardContent>
        </Card>
      )}

      <div className="p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
        <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
        <div className="text-secondary text-sm">{config.info}</div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${category}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">{editingItem ? 'Edit' : 'Add'} {config.title.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Label / Meaning</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={config.labelPlaceholder} className="bg-background border-border" required data-testid={`${category}-label`} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Word in {conlang?.name}</Label>
                <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="The word" className="bg-background border-border font-conlang" required data-testid={`${category}-word`} />
              </div>
              <div className="space-y-2">
                <Label>IPA (optional)</Label>
                <Input value={ipa} onChange={(e) => setIpa(e.target.value)} placeholder="/.../" className="bg-background border-border font-conlang" data-testid={`${category}-ipa`} />
              </div>
            </div>

            {config.extraFields.includes('person') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Person</Label>
                  <Input value={person} onChange={(e) => setPerson(e.target.value)} placeholder="e.g., 1st, 2nd, 3rd" className="bg-background border-border" />
                </div>
                <div className="space-y-2">
                  <Label>Number</Label>
                  <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="e.g., singular, plural" className="bg-background border-border" />
                </div>
              </div>
            )}
            {config.extraFields.includes('gender') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Input value={gender} onChange={(e) => setGender(e.target.value)} placeholder="e.g., masculine, feminine" className="bg-background border-border" />
                </div>
                <div className="space-y-2">
                  <Label>Case</Label>
                  <Input value={caseField} onChange={(e) => setCaseField(e.target.value)} placeholder="e.g., nominative, accusative" className="bg-background border-border" />
                </div>
              </div>
            )}
            {config.extraFields.includes('numeric_value') && (
              <div className="space-y-2">
                <Label>Numeric Value</Label>
                <Input type="number" value={numericValue} onChange={(e) => setNumericValue(e.target.value)} placeholder="e.g., 1, 2, 3" className="bg-background border-border" />
              </div>
            )}
            {config.extraFields.includes('relationship_type') && (
              <div className="space-y-2">
                <Label>Relationship Type</Label>
                <Input value={relationshipType} onChange={(e) => setRelationshipType(e.target.value)} placeholder="e.g., parent, sibling, child, spouse" className="bg-background border-border" />
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." className="bg-background border-border" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !label || !word} data-testid={`submit-${category}`}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingItem ? 'Save' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LexiconCategoryPage;
